import type { OpenAPIVoidResponse, OpenAPIVoidAwaitedResponse } from './api-switcher'
import { client } from './client'

/** Two-phase photo upload (design doc 2026-07-06, FEATURE_TWO_PHASE_UPLOAD):
 * 1. reserve an upload slot (`POST /v2/photo`) — quota errors surface here
 *    with the same codes the V1 endpoint used (1401/1406/…), so the
 *    caller's error mapping is unchanged;
 * 2. upload the binary straight to S3 with the presigned POST.
 *
 * Success is the durable S3 write (owner decision 2026-07-07): the finalize
 * Lambda is guaranteed-delivery (SQS + DLQ), so the client does not poll
 * per photo — the photo appears in lists once finalize publishes it,
 * typically within seconds. Trade-off, accepted: a finalize-side rejection
 * (e.g. an undecodable file) no longer surfaces as an upload error; the
 * photo simply never appears and the reservation expires.
 *
 * The signature mirrors PhotoAPI.uploadPhoto; width/height are not sent —
 * the backend decodes the real dimensions and never trusts the client's.
 */
export const uploadPhotoTwoPhase = async (
  file: File,
  last_modified: number,
  utc_offset_minutes: number,
  signal: AbortSignal
): OpenAPIVoidResponse => {
  // Step 1: reserve. Error bodies pass through untouched so ImageUploader's
  // code→message mapping (1401 storage_over, 1406 file_count_over, …) keeps
  // working.
  const reserved = await client.POST('/v2/photo', {
    body: {
      bytes: file.size,
      last_modified_at: last_modified,
      utc_offset_minutes,
      content_type: file.type as 'image/png' | 'image/jpeg' | 'image/avif',
    },
    signal,
  })
  if (reserved.error || !reserved.data) {
    return { error: reserved.error, response: reserved.response } as OpenAPIVoidAwaitedResponse
  }

  const { upload } = reserved.data

  // Step 2: S3 presigned POST. Policy fields first, the file strictly last —
  // S3 ignores any form field after `file`.
  const form = new FormData()
  for (const [name, value] of Object.entries(upload.fields)) {
    form.append(name, value)
  }
  form.append('file', file)

  const s3Response = await fetch(upload.url, { method: 'POST', body: form, signal })
  if (!s3Response.ok) {
    return {
      error: { code: 1000, message: `failed to upload binary to storage (${s3Response.status})` },
      response: s3Response,
    }
  }

  return { response: s3Response }
}
